import { useState, useEffect } from 'react';
import { useAccount, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { parseUnits, formatUnits } from 'viem';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Navigation from "@/components/Navigation";
import { Send, ArrowLeft, CheckCircle, AlertCircle, User, Loader2 } from 'lucide-react';
import { TOKENS, ERC20_ABI } from '@/config/wagmi';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SERVICE_FEE_PERCENTAGE = 0.48; // 0.48% platform fee

export default function NonCustodialSendPage({ user, onLogout }) {
  const { address, isConnected } = useAccount();
  const [step, setStep] = useState(1); // 1: Form, 2: Review, 3: Sending, 4: Success
  
  const [formData, setFormData] = useState({
    recipient: '',
    amount: '',
    currency: 'USDT',
    note: ''
  });

  const [quote, setQuote] = useState(null);
  const [estimatedGas, setEstimatedGas] = useState('0.0015'); // Approximate
  const [isResolvingUsername, setIsResolvingUsername] = useState(false);
  const [resolvedAddress, setResolvedAddress] = useState(null);
  const [serviceFee, setServiceFee] = useState(0);

  // Write contract hook for sending
  const { data: txData, writeContract, isPending, isError, error } = useWriteContract();

  // Wait for transaction receipt
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash: txData,
  });

  useEffect(() => {
    if (isSuccess && step === 3) {
      setStep(4); // Move to success screen
      toast.success('Transfer completed!');
    }
  }, [isSuccess, step]);

  useEffect(() => {
    if (isError) {
      toast.error(error?.message || 'Transaction failed');
    }
  }, [isError, error]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Calculate service fee when amount changes
    if (name === 'amount' && value) {
      const amountNum = parseFloat(value);
      if (!isNaN(amountNum)) {
        const fee = (amountNum * SERVICE_FEE_PERCENTAGE) / 100;
        setServiceFee(fee);
      }
    }
    
    // Reset resolved address when recipient changes
    if (name === 'recipient') {
      setResolvedAddress(null);
    }
  };

  const validateAddress = (addr) => {
    return /^0x[a-fA-F0-9]{40}$/.test(addr);
  };

  // Resolve @username to wallet address
  const resolveUsername = async (identifier) => {
    if (!identifier.startsWith('@')) {
      return identifier; // Already a wallet address
    }

    try {
      setIsResolvingUsername(true);
      const response = await axios.get(`${API}/crypto/username/lookup/${identifier}`);
      
      if (response.data && response.data.wallet_address) {
        setResolvedAddress(response.data.wallet_address);
        toast.success(`Found ${identifier} - ${response.data.wallet_address.slice(0, 10)}...`);
        return response.data.wallet_address;
      } else {
        toast.error(`Username ${identifier} not found`);
        return null;
      }
    } catch (error) {
      console.error('Error resolving username:', error);
      toast.error(`Could not find ${identifier}`);
      return null;
    } finally {
      setIsResolvingUsername(false);
    }
  };

  const handleGetQuote = async () => {
    const { recipient, amount, currency } = formData;

    // Validation
    if (!recipient || !amount) {
      toast.error('Please fill in all fields');
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      toast.error('Invalid amount');
      return;
    }

    // Resolve @username or validate wallet address
    let finalRecipient = recipient;
    
    if (recipient.startsWith('@')) {
      finalRecipient = await resolveUsername(recipient);
      if (!finalRecipient) {
        return; // Username resolution failed
      }
    } else if (!validateAddress(recipient)) {
      toast.error('Invalid wallet address. Use @username or 0x... address');
      return;
    }

    // Calculate service fee (0.48%)
    const fee = (amountNum * SERVICE_FEE_PERCENTAGE) / 100;
    const amountAfterFee = amountNum - fee;

    // Create quote
    setQuote({
      recipient: finalRecipient,
      displayRecipient: recipient, // Show original @username
      amount: amountNum,
      serviceFee: fee,
      amountAfterFee: amountAfterFee,
      currency,
      estimatedGas: estimatedGas,
      total: amountNum
    });

    setStep(2);
  };

  const handleSend = async () => {
    if (!isConnected) {
      toast.error('Please connect your wallet first');
      return;
    }

    try {
      setStep(3); // Move to sending state

      const token = TOKENS[formData.currency];
      // Send amount AFTER service fee deduction
      const amountWei = parseUnits(quote.amountAfterFee.toString(), token.decimals);

      // Call transfer function on ERC20 contract
      writeContract({
        address: token.address,
        abi: ERC20_ABI,
        functionName: 'transfer',
        args: [quote.recipient, amountWei],
      });

    } catch (err) {
      console.error('Send error:', err);
      toast.error('Failed to send transfer. Please try again.');
      setStep(2); // Go back to review
    }
  };

  // Save transaction to database after success
  useEffect(() => {
    const saveTransaction = async () => {
      if (isSuccess && txData?.hash && quote) {
        try {
          const token = localStorage.getItem('routeflow_token');
          await axios.post(`${API}/transactions/save`, {
            user_id: user.id,
            from_address: address,
            to_address: quote.recipient,
            to_identifier: quote.displayRecipient,
            amount: quote.amountAfterFee,
            service_fee: quote.serviceFee,
            currency: quote.currency,
            tx_hash: txData.hash,
            note: formData.note
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('Transaction saved to database');
        } catch (error) {
          console.error('Failed to save transaction:', error);
          // Don't show error to user - transaction was successful on blockchain
        }
      }
    };
    
    saveTransaction();
  }, [isSuccess, txData, quote, user, address, formData.note]);

  const handleReset = () => {
    setFormData({
      recipient: '',
      amount: '',
      currency: 'USDT',
      note: ''
    });
    setQuote(null);
    setStep(1);
  };

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <Navigation user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-16">
          <Card className="max-w-md mx-auto border-2 border-emerald-200">
            <CardHeader>
              <CardTitle className="text-center">Connect Your Wallet</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-gray-600 mb-4">
                Please connect your wallet to send money
              </p>
              <Button onClick={() => window.location.href = '/crypto/wallet'}>
                Go to Wallet
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => step > 1 ? setStep(step - 1) : window.location.href = '/crypto/wallet'}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Send Money
            </h1>
            <p className="text-gray-600 mt-1">
              {step === 1 && 'Enter recipient and amount'}
              {step === 2 && 'Review your transaction'}
              {step === 3 && 'Confirm in your wallet'}
              {step === 4 && 'Transaction sent!'}
            </p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Step 1: Form */}
          {step === 1 && (
            <Card className="border-2 border-emerald-200 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5 text-emerald-600" />
                  Send Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Recipient */}
                <div>
                  <Label htmlFor="recipient">Send To</Label>
                  <div className="relative">
                    <Input
                      id="recipient"
                      name="recipient"
                      placeholder="@username or 0x..."
                      value={formData.recipient}
                      onChange={handleChange}
                      className="mt-2 pr-10"
                    />
                    {formData.recipient.startsWith('@') && (
                      <User className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Use @username or wallet address (0x...)
                  </p>
                  {resolvedAddress && (
                    <p className="text-xs text-emerald-600 mt-1 font-medium">
                      ✓ Resolved to: {resolvedAddress.slice(0, 10)}...{resolvedAddress.slice(-8)}
                    </p>
                  )}
                </div>

                {/* Amount */}
                <div>
                  <Label htmlFor="amount">Amount</Label>
                  <Input
                    id="amount"
                    name="amount"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={handleChange}
                    className="mt-2"
                  />
                </div>

                {/* Currency */}
                <div>
                  <Label htmlFor="currency">Currency</Label>
                  <select
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className="mt-2 w-full px-3 py-2 border rounded-md"
                  >
                    <option value="USDT">USDT</option>
                    <option value="USDC">USDC</option>
                  </select>
                </div>

                {/* Note */}
                <div>
                  <Label htmlFor="note">Note (Optional)</Label>
                  <Input
                    id="note"
                    name="note"
                    placeholder="What's this for?"
                    value={formData.note}
                    onChange={handleChange}
                    className="mt-2"
                  />
                </div>

                <Button
                  onClick={handleGetQuote}
                  disabled={isResolvingUsername}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                  size="lg"
                >
                  {isResolvingUsername ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Looking up {formData.recipient}...
                    </>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Review */}
          {step === 2 && quote && (
            <Card className="border-2 border-emerald-200 shadow-lg">
              <CardHeader>
                <CardTitle>Review Transaction</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sending To:</span>
                    <span className="font-medium text-sm">
                      {quote.displayRecipient}
                      {quote.displayRecipient.startsWith('@') && (
                        <User className="inline w-3 h-3 ml-1 text-emerald-600" />
                      )}
                    </span>
                  </div>
                  {quote.displayRecipient.startsWith('@') && (
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Wallet:</span>
                      <span className="font-mono text-gray-500">{quote.recipient.slice(0, 10)}...{quote.recipient.slice(-8)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-bold text-lg">{quote.amount} {quote.currency}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Service Fee (0.48%):</span>
                    <span className="text-red-600">-{quote.serviceFee.toFixed(4)} {quote.currency}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Network Fee (est.):</span>
                    <span className="text-gray-600">~$0.001</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between">
                    <span className="font-semibold">Recipient Receives:</span>
                    <span className="font-bold text-emerald-600">{quote.amountAfterFee.toFixed(4)} {quote.currency}</span>
                  </div>
                </div>

                {formData.note && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-600">Note: {formData.note}</p>
                  </div>
                )}

                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    ⚠️ You'll need to approve this in your wallet app. A tiny network fee (~$0.001) will be charged from your MATIC balance.
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleSend}
                    disabled={isPending}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500"
                  >
                    {isPending ? 'Preparing...' : 'Send Now'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Sending */}
          {step === 3 && (
            <Card className="border-2 border-blue-200 shadow-lg">
              <CardContent className="text-center py-12">
                <div className="animate-spin w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-6"></div>
                <h3 className="text-2xl font-bold mb-2">
                  {isConfirming ? 'Confirming...' : 'Check Your Wallet'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {isConfirming 
                    ? 'Waiting for blockchain confirmation...'
                    : 'Please approve the transaction in your wallet app'
                  }
                </p>
                {txData?.hash && (
                  <p className="text-xs text-gray-500 mt-4">
                    Transaction: {txData.hash.slice(0, 10)}...{txData.hash.slice(-8)}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <Card className="border-2 border-green-200 shadow-lg">
              <CardContent className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-green-600 mb-2">
                  Transaction Sent!
                </h3>
                <p className="text-gray-600 mb-6">
                  Your {quote?.amount} {quote?.currency} has been sent successfully
                </p>
                
                {txData?.hash && (
                  <div className="bg-gray-50 p-4 rounded-lg mb-6">
                    <p className="text-sm text-gray-600 mb-2">Transaction Hash:</p>
                    <p className="text-xs font-mono break-all">{txData.hash}</p>
                    <Button
                      variant="link"
                      onClick={() => window.open(`https://polygonscan.com/tx/${txData.hash}`, '_blank')}
                      className="mt-2"
                    >
                      View on PolygonScan →
                    </Button>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => window.location.href = '/crypto/wallet'}
                    className="flex-1"
                  >
                    Back to Wallet
                  </Button>
                  <Button
                    onClick={handleReset}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500"
                  >
                    Send Again
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
